import { Calendar, Home, MapPin } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

/**
 * Every field below the client's name is optional, and a field that is not
 * supplied takes its line with it rather than leaving a gap or a placeholder:
 * the designer requests list joins a property that a project need not have, so
 * a real request can arrive with no city, no size and no budget.
 */
interface RequestCardProps {
	clientName: string;
	clientAvatar?: string;
	/** A short property descriptor, shown beside the size. */
	area?: string;
	/** The property's city. */
	location?: string;
	/** The property's size in sq ft. */
	areaSize?: number;
	/** A date or timeline line, shown beside the calendar icon. */
	timeline?: string;
	/** A short label shown opposite the budget, such as the request's status. */
	badge?: string;
	budgetRange?: string;
	onSendQuote?: () => void;
	isNew?: boolean;
}

export function RequestCard({
	clientName,
	clientAvatar,
	area,
	location,
	areaSize,
	timeline,
	badge,
	budgetRange,
	onSendQuote,
	isNew,
}: RequestCardProps) {
	const propertyLine = [
		areaSize === undefined ? null : `${areaSize.toLocaleString()} sq ft`,
		area,
	]
		.filter(Boolean)
		.join(" • ");

	return (
		<FloatPanel
			elevation={1}
			padding={16}
			style={{ background: "var(--color-dashboard-paper)", marginBottom: 12 }}
		>
			<div className="flex items-start justify-between">
				<div className="flex items-center gap-2.5">
					<Avatar
						className="h-9 w-9"
						style={{ border: "1px solid var(--color-dashboard-border)" }}
					>
						{clientAvatar ? (
							<img src={clientAvatar} alt={clientName} />
						) : (
							<div className="flex h-full w-full items-center justify-center bg-[var(--color-dashboard-divider)] text-sm font-semibold text-[var(--color-dashboard-accent-dark)]">
								{clientName.charAt(0)}
							</div>
						)}
					</Avatar>
					<div>
						<h3
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 14,
								fontWeight: 600,
								color: "var(--color-dashboard-text)",
							}}
						>
							{clientName}
						</h3>
						{propertyLine && (
							<div
								className="flex items-center gap-1.5"
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
									marginTop: 2,
								}}
							>
								<Home className="h-3 w-3" />
								<span>{propertyLine}</span>
							</div>
						)}
					</div>
				</div>
				{isNew && (
					<span
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 9,
							letterSpacing: "0.08em",
							fontWeight: 600,
							background:
								"color-mix(in srgb, var(--color-dashboard-pill-done) 10%, transparent)",
							color: "var(--color-dashboard-pill-done)",
							padding: "4px 8px",
							borderRadius: 14,
						}}
					>
						NEW
					</span>
				)}
			</div>

			{(location || timeline) && (
				<div
					className="mt-3 flex items-center gap-4 text-xs text-[var(--color-dashboard-muted)]"
					style={{ fontFamily: "var(--font-body)" }}
				>
					{location && (
						<div className="flex items-center gap-1">
							<MapPin
								className="h-3 w-3"
								style={{ color: "var(--color-dashboard-accent-dark)" }}
							/>
							<span>{location}</span>
						</div>
					)}
					{timeline && (
						<div className="flex items-center gap-1">
							<Calendar
								className="h-3 w-3"
								style={{ color: "var(--color-dashboard-accent-dark)" }}
							/>
							<span>{timeline}</span>
						</div>
					)}
				</div>
			)}

			{(budgetRange || badge) && (
				<div
					className="mt-3 flex items-center justify-between"
					style={{ fontFamily: "var(--font-body)" }}
				>
					{budgetRange && (
						<span
							style={{
								fontSize: 13,
								fontWeight: 600,
								color: "var(--color-dashboard-text)",
							}}
						>
							{budgetRange}
						</span>
					)}
					{badge && (
						<span
							style={{ fontSize: 11, color: "var(--color-dashboard-muted)" }}
						>
							{badge}
						</span>
					)}
				</div>
			)}

			{onSendQuote && (
				<StudioButton
					variant="primary"
					size="sm"
					onClick={onSendQuote}
					style={{ width: "100%", marginTop: 12 }}
				>
					Send Quote
				</StudioButton>
			)}
		</FloatPanel>
	);
}
